import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { AdminNavigation } from "@/components/admin/AdminNavigation";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, Users, BookOpen, Heart, Trophy, Eye } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { UserJournalDialog } from "@/components/admin/UserJournalDialog";
import { UserPrayerDialog } from "@/components/admin/UserPrayerDialog";

interface UserData {
  id: string;
  email: string;
  full_name: string;
  subscription_status: string;
  bible_version: string;
  congregation_name: string;
  created_at: string;
  journal_entries_count: number;
  prayer_requests_count: number;
  total_points: number;
  last_sign_in: string;
}

const UsersPage = () => {
  const [users, setUsers] = useState<UserData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedUserName, setSelectedUserName] = useState<string>("");
  const [journalDialogOpen, setJournalDialogOpen] = useState(false);
  const [prayerDialogOpen, setPrayerDialogOpen] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    checkAdminAndFetchUsers();
  }, []);

  const checkAdminAndFetchUsers = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        navigate("/");
        return;
      }

      // Check admin status
      const { data: roleData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .maybeSingle();

      if (!roleData) {
        toast({
          title: "Access Denied",
          description: "You don't have admin privileges",
          variant: "destructive",
        });
        navigate("/");
        return;
      }

      // Fetch all users data with available information
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("*")
        .order("created_at", { ascending: false });

      if (profilesError) throw profilesError;

      // Get additional data for each user
      const usersData = await Promise.all(
        profiles.map(async (profile) => {
          // Get journal entries count
          const { count: journalCount } = await supabase
            .from("journal_entries")
            .select("*", { count: "exact", head: true })
            .eq("user_id", profile.id);

          // Get prayer requests count
          const { count: prayerCount } = await supabase
            .from("prayer_requests")
            .select("*", { count: "exact", head: true })
            .eq("user_id", profile.id);

          // Get total points
          const { data: pointsData } = await supabase
            .from("user_points")
            .select("total_points")
            .eq("user_id", profile.id)
            .maybeSingle();

          return {
            id: profile.id,
            email: profile.id.substring(0, 8) + "...", // Show partial ID for reference
            full_name: profile.full_name || "N/A",
            subscription_status: profile.subscription_status || "free",
            bible_version: profile.bible_version || "KJV",
            congregation_name: profile.congregation_name || "N/A",
            created_at: profile.created_at,
            journal_entries_count: journalCount || 0,
            prayer_requests_count: prayerCount || 0,
            total_points: pointsData?.total_points || 0,
            last_sign_in: "See Supabase Dashboard",
          };
        })
      );

      setUsers(usersData);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast({
        title: "Error",
        description: "Failed to load user data",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive"> = {
      free: "secondary",
      premium: "default",
      church: "default",
    };
    return <Badge variant={variants[status] || "secondary"}>{status}</Badge>;
  };

  const handleViewJournals = (userId: string, userName: string) => {
    setSelectedUserId(userId);
    setSelectedUserName(userName);
    setJournalDialogOpen(true);
  };

  const handleViewPrayers = (userId: string, userName: string) => {
    setSelectedUserId(userId);
    setSelectedUserName(userName);
    setPrayerDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-app-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-app-background pb-24">
      <div className="container py-8">
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <Users className="h-6 w-6 text-primary" />
            <h1 className="text-3xl font-bold text-app-text-primary">User Management</h1>
          </div>
          <p className="text-app-text-secondary">
            View and manage all users and their activity. For full user details including emails, visit the Supabase Dashboard.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="p-6 bg-app-surface border-app-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-app-text-secondary">Total Users</p>
                <p className="text-2xl font-bold text-app-text-primary">{users.length}</p>
              </div>
              <Users className="h-8 w-8 text-primary opacity-50" />
            </div>
          </Card>

          <Card className="p-6 bg-app-surface border-app-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-app-text-secondary">Premium Users</p>
                <p className="text-2xl font-bold text-app-text-primary">
                  {users.filter(u => u.subscription_status !== "free").length}
                </p>
              </div>
              <Trophy className="h-8 w-8 text-primary opacity-50" />
            </div>
          </Card>

          <Card className="p-6 bg-app-surface border-app-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-app-text-secondary">Total Journals</p>
                <p className="text-2xl font-bold text-app-text-primary">
                  {users.reduce((sum, u) => sum + u.journal_entries_count, 0)}
                </p>
              </div>
              <BookOpen className="h-8 w-8 text-primary opacity-50" />
            </div>
          </Card>

          <Card className="p-6 bg-app-surface border-app-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-app-text-secondary">Prayer Requests</p>
                <p className="text-2xl font-bold text-app-text-primary">
                  {users.reduce((sum, u) => sum + u.prayer_requests_count, 0)}
                </p>
              </div>
              <Heart className="h-8 w-8 text-primary opacity-50" />
            </div>
          </Card>
        </div>

        <Card className="bg-app-surface border-app-border">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-app-text-secondary">User ID</TableHead>
                  <TableHead className="text-app-text-secondary">Name</TableHead>
                  <TableHead className="text-app-text-secondary">Status</TableHead>
                  <TableHead className="text-app-text-secondary">Congregation</TableHead>
                  <TableHead className="text-app-text-secondary">Journals</TableHead>
                  <TableHead className="text-app-text-secondary">Prayers</TableHead>
                  <TableHead className="text-app-text-secondary">Points</TableHead>
                  <TableHead className="text-app-text-secondary">Joined</TableHead>
                  <TableHead className="text-app-text-secondary">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium text-app-text-primary">
                      {user.email}
                    </TableCell>
                    <TableCell className="text-app-text-secondary">
                      {user.full_name}
                    </TableCell>
                    <TableCell>{getStatusBadge(user.subscription_status)}</TableCell>
                    <TableCell className="text-app-text-secondary">
                      {user.congregation_name}
                    </TableCell>
                    <TableCell className="text-app-text-secondary">
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => handleViewJournals(user.id, user.full_name)}
                        disabled={user.journal_entries_count === 0}
                        className="h-auto p-0"
                      >
                        {user.journal_entries_count}
                      </Button>
                    </TableCell>
                    <TableCell className="text-app-text-secondary">
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => handleViewPrayers(user.id, user.full_name)}
                        disabled={user.prayer_requests_count === 0}
                        className="h-auto p-0"
                      >
                        {user.prayer_requests_count}
                      </Button>
                    </TableCell>
                    <TableCell className="text-app-text-secondary">
                      {user.total_points}
                    </TableCell>
                    <TableCell className="text-app-text-secondary text-sm">
                      {new Date(user.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewJournals(user.id, user.full_name)}
                          disabled={user.journal_entries_count === 0}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>

        {selectedUserId && (
          <>
            <UserJournalDialog
              userId={selectedUserId}
              userName={selectedUserName}
              open={journalDialogOpen}
              onOpenChange={setJournalDialogOpen}
            />
            <UserPrayerDialog
              userId={selectedUserId}
              userName={selectedUserName}
              open={prayerDialogOpen}
              onOpenChange={setPrayerDialogOpen}
            />
          </>
        )}
      </div>
      <AdminNavigation />
    </div>
  );
};

export default UsersPage;
